/**
 * PIN Login Page for KDS Tablets
 * Staff enter their 6-digit PIN to log into a station
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, 
  ChefHat, 
  GlassWater, 
  Users, 
  Tablet, 
  Monitor,
  Lock,
  Delete,
  Sun,
  Moon
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Station configurations
const STATION_CONFIG = {
  pos: { icon: Monitor, label: 'POS Terminal', color: 'green', route: '/pos' },
  counter: { icon: Users, label: 'Counter Display', color: 'orange', route: '/kds/counter' },
  barista: { icon: Coffee, label: 'Barista Station', color: 'blue', route: '/kds/barista' },
  kitchen: { icon: ChefHat, label: 'Kitchen Station', color: 'red', route: '/kds/kitchen' },
  juicer: { icon: GlassWater, label: 'Juice Bar', color: 'yellow', route: '/kds/juicer' },
  server: { icon: Tablet, label: 'Server Tablet', color: 'purple', route: '/kds/server' }
};

const COLOR_CLASSES = {
  green: 'from-green-500 to-green-600',
  orange: 'from-orange-500 to-orange-600',
  blue: 'from-blue-500 to-blue-600',
  red: 'from-red-500 to-red-600',
  yellow: 'from-yellow-500 to-yellow-600',
  purple: 'from-purple-500 to-purple-600'
};

export default function PinLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device') || 'SRV-01';
  
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const inputRef = useRef(null);

  // Load device info
  useEffect(() => {
    const fetchDevice = async () => {
      try {
        await api.get('/api/devices/station-types');
        // For now, determine device type from device ID
        const stationType = deviceId.startsWith('POS') ? 'pos' :
                            deviceId.startsWith('KDS-CTR') ? 'counter' :
                            deviceId.startsWith('KDS-BAR') ? 'barista' :
                            deviceId.startsWith('KDS-KIT') ? 'kitchen' :
                            deviceId.startsWith('KDS-JUI') ? 'juicer' : 'server';
        
        setDevice({
          device_id: deviceId,
          station_type: stationType,
          ...STATION_CONFIG[stationType]
        });
      } catch (error) {
        console.error('Error fetching device:', error);
      }
    };
    fetchDevice();
  }, [deviceId]);

  // Check saved theme
  useEffect(() => {
    const saved = localStorage.getItem('kds-dark-mode');
    if (saved === 'true') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('kds-dark-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('kds-dark-mode', 'false');
    }
  };

  // Auto-focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle PIN input
  const handlePinInput = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      
      // Auto-submit when 6 digits entered
      if (newPin.length === 6) {
        handleLogin(newPin);
      }
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  const handleLogin = async (pinCode) => {
    setLoading(true);
    try {
      const response = await api.post('/api/device-sessions/login', {
        device_id: deviceId,
        pin: pinCode
      });

      const data = response.data;

      // Store session info
      localStorage.setItem('kds-session', JSON.stringify(data.session));
      localStorage.setItem('kds-staff', JSON.stringify(data.staff));
      localStorage.setItem('kds-device', JSON.stringify(data.device));
      
      toast.success(`Welcome, ${data.staff.name}!`);
      
      // Navigate to appropriate station
      const stationType = data.device.station_type;
      const config = STATION_CONFIG[stationType];
      if (config) {
        navigate(`${config.route}?device=${deviceId}`);
      } else {
        navigate('/kds/counter');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid PIN');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Send heartbeat every minute
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await api.post('/api/devices/heartbeat', { device_id: deviceId });
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };
    
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [deviceId]);

  const stationConfig = device ? STATION_CONFIG[device.station_type] : null;
  const Icon = stationConfig?.icon || Tablet;
  const colorClass = stationConfig?.color ? COLOR_CLASSES[stationConfig.color] : COLOR_CLASSES.purple;

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-gray-900 dark:text-white">{device?.label || 'Station'}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{deviceId}</p>
          </div>
        </div>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {isDark ? (
            <Sun className="w-5 h-5 text-yellow-500" />
          ) : (
            <Moon className="w-5 h-5 text-gray-600" />
          )}
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          {/* Lock Icon */}
          <div className="text-center mb-8">
            <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center mb-4`}>
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Enter PIN</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Enter your 6-digit PIN to continue</p>
          </div>

          {/* PIN Display */}
          <div className="flex justify-center gap-3 mb-8">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: pin.length > i ? 1.1 : 1,
                  backgroundColor: pin.length > i 
                    ? (isDark ? '#3b82f6' : '#2563eb')
                    : (isDark ? '#374151' : '#e5e7eb')
                }}
                className="w-12 h-12 rounded-xl flex items-center justify-center"
              >
                {pin.length > i && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-4 h-4 rounded-full bg-white"
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Number Pad */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, 'Del'].map((key) => (
              <motion.button
                key={key}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
                onClick={() => {
                  if (key === 'C') handleClear();
                  else if (key === 'Del') handleDelete();
                  else handlePinInput(String(key));
                }}
                className={`h-16 rounded-xl text-xl font-semibold transition-colors ${
                  key === 'C' 
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                    : key === 'Del'
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                } disabled:opacity-50`}
              >
                {key === 'Del' ? <Delete className="w-6 h-6 mx-auto" /> : key}
              </motion.button>
            ))}
          </div>

          {/* Loading Indicator */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-3xl"
              >
                <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Bulaccino Cafe - Station Management System
        </p>
      </footer>
    </div>
  );
}
